import { Link } from "react-router-dom";
import { Users, UserPlus, CreditCard, Repeat } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { useFetch, PageHeader, StatCard, Panel, Empty, Loading, ErrorBox, fmtDate } from "@/admin/ui";

const Chart = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data.map((d) => ({ ...d, dia: d.date.slice(8) + "/" + d.date.slice(5, 7) }))} margin={{ left: -20, right: 8 }}>
      <CartesianGrid vertical={false} stroke="#E2E8F0" />
      <XAxis dataKey="dia" tick={{ fontSize: 11, fill: "#718096" }} interval={4} tickLine={false} axisLine={false} />
      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#718096" }} tickLine={false} axisLine={false} />
      <Tooltip cursor={{ fill: "#F1F5F9" }} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="altas" name="Altas" fill="#2E9E5B" radius={[4, 4, 0, 0]} />
      <Bar dataKey="pagos" name="Pagos" fill="#94A3B8" radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const TopContent = ({ items }) => (!items.length ? <Empty text="Aún no hay visitas a módulos registradas." testid="admin-top-empty" /> : (
  <div>
    {items.map((t, i) => (
      <div key={t.module_id} className="adm-list-item" data-testid={`admin-top-item-${i}`}>
        <span><b style={{ color: "#718096", marginRight: 8 }}>{i + 1}</b>{t.title}</span>
        <span className="adm-sub">{t.views} {t.views === 1 ? "visita" : "visitas"} · {t.people} {t.people === 1 ? "persona" : "personas"}</span>
      </div>
    ))}
  </div>
));

const RecentAccess = ({ items }) => (!items.length ? <Empty text="Aún no hay accesos registrados." testid="admin-recent-empty" /> : (
  <div className="adm-table-wrap">
    <table className="adm-table">
      <thead><tr><th>Alumno</th><th>Dispositivo</th><th>Fecha</th></tr></thead>
      <tbody>{items.map((r, i) => (
        <tr key={i} data-testid={`admin-recent-row-${i}`}>
          <td>{r.user_id ? <Link to={`/admin/alumnos/${r.user_id}`} style={{ color: "inherit" }}>{r.email}</Link> : r.email}</td>
          <td className="adm-sub">{r.device}</td><td className="adm-sub">{fmtDate(r.created_at, true)}</td>
        </tr>))}
      </tbody>
    </table>
  </div>
));

export default function AdminDashboard() {
  const { data, error } = useFetch("/admin/stats");
  if (error) return <ErrorBox />;
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" subtitle="Resumen general de la academia · últimos 30 días" />
      <div className="adm-grid adm-kpis" style={{ marginBottom: "1rem" }}>
        <StatCard label="Alumnos activos" value={data.active_students} hint={`de ${data.total_students} cuentas`} icon={Users} testid="admin-kpi-active" />
        <StatCard label="Nuevas altas" value={data.new_signups} hint="últimos 30 días" icon={UserPlus} testid="admin-kpi-signups" />
        <StatCard label="Pagos realizados / fallidos" value={`${data.payments_paid} / ${data.payments_failed}`} hint="últimos 30 días" icon={CreditCard} testid="admin-kpi-payments" />
        <StatCard label="Tasa de recurrencia" value={`${data.recurrence_rate}%`} hint="siguen pagando de quienes pagaron alguna vez" icon={Repeat} testid="admin-kpi-recurrence" />
      </div>
      <div className="adm-grid adm-two" style={{ marginBottom: "1rem" }}>
        <Panel title="Altas y pagos por día" testid="admin-chart"><Chart data={data.chart} /></Panel>
        <Panel title="Top contenidos vistos" testid="admin-top-content"><TopContent items={data.top_content} /></Panel>
      </div>
      <Panel title="Últimos accesos" action={<Link to="/admin/accesos" className="adm-btn sm" data-testid="admin-see-access">Ver auditoría</Link>} testid="admin-recent-access">
        <RecentAccess items={data.recent_access} />
      </Panel>
    </div>
  );
}
